import { CommonModule } from '@angular/common'
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatMenuTrigger } from '@angular/material/menu'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { CanvasService } from '../canvas.service'
import { AddItemCommand } from '../commands/add-item.command'
import { CanvasArgs, CanvasCommand } from '../commands/canvas-command.model'
import { DeleteCanvasCommand } from '../commands/delete-canvas.command'
import { RedoDiagramCommand } from '../commands/redo-diagram.command'
import { SaveCanvasCommand } from '../commands/save-canvas.command'
import { UndoDiagramCommand } from '../commands/undo-diagram.command'
import { DdcCommandDialogInvokerComponent } from '../ddc-command-dialog-invoker/ddc-command-dialog-invoker.component'
import { DdcCommandInvokerComponent } from '../ddc-command-invoker/ddc-command-invoker.component'
import { CanvasData } from '../models/canvas.model'

@Component({
  selector: 'ddc-canvas-toolbar',
  templateUrl: './ddc-canvas-toolbar.component.html',
  styleUrls: ['./ddc-canvas-toolbar.component.scss'],
  imports: [
    CommonModule,
    AngularMaterialsModules,
    DdcCommandInvokerComponent,
    DdcCommandDialogInvokerComponent,
  ],
  standalone: true,
})
export class DdcCanvasToolbarComponent {
  private readonly destroyRef = inject(DestroyRef)

  @Input() canvasTitle: string = ''
  @Input() hasUnsavedChanges: boolean = false
  @Input() isSaving: boolean = false
  @Input() canvasId: number | null = null
  @Input() canUndo: boolean = false
  @Input() canRedo: boolean = false
  /** Provides the current diagram data for the save command. */
  @Input() getCanvasData?: () => CanvasData | null

  /** Client-side actions delegated to the parent. */
  @Output() toggleSidebar = new EventEmitter<void>()
  @Output() addItem = new EventEmitter<void>()
  @Output() undo = new EventEmitter<void>()
  @Output() redo = new EventEmitter<void>()
  /** Fires after a service command (save/delete) completes so the parent can react. */
  @Output() commandExecuted = new EventEmitter<CanvasCommand>()

  @ViewChild(MatMenuTrigger) contextMenuTrigger: MatMenuTrigger

  contextMenuPosition = { x: 0, y: 0 }

  readonly saveCommand: SaveCanvasCommand
  readonly addItemCommand: AddItemCommand
  readonly undoCommand: UndoDiagramCommand
  readonly redoCommand: RedoDiagramCommand
  readonly deleteCommand: DeleteCanvasCommand
  readonly commands: CanvasCommand[]

  constructor(canvasService: CanvasService) {
    this.saveCommand = new SaveCanvasCommand(canvasService)
    this.addItemCommand = new AddItemCommand(canvasService)
    this.undoCommand = new UndoDiagramCommand(canvasService)
    this.redoCommand = new RedoDiagramCommand(canvasService)
    this.deleteCommand = new DeleteCanvasCommand(canvasService)
    this.commands = [
      this.saveCommand,
      this.addItemCommand,
      this.undoCommand,
      this.redoCommand,
      this.deleteCommand,
    ]
  }

  /** Build the arguments for a command from the current toolbar state. */
  argsFor(command: CanvasCommand): CanvasArgs {
    const base: CanvasArgs = {
      canvasId: this.canvasId,
      canvasTitle: this.canvasTitle,
      hasUnsavedChanges: this.hasUnsavedChanges,
      isSaving: this.isSaving,
      canUndo: this.canUndo,
      canRedo: this.canRedo,
    }

    switch (command.id) {
      case 'save':
        return { ...base, data: this.getCanvasData?.() ?? undefined }
      case 'add-item':
        return { ...base, action: () => this.addItem.emit() }
      case 'undo':
        return { ...base, action: () => this.undo.emit() }
      case 'redo':
        return { ...base, action: () => this.redo.emit() }
      default:
        return base
    }
  }

  /** Follow-up after a command has executed (invoked by the invoker's output). */
  onCommandExecuted(command: CanvasCommand): void {
    if (command.id === 'save' || command.id === 'delete') {
      this.commandExecuted.emit(command)
    }
  }

  /** Execute a command directly (context menu, keyboard) then run follow-up. */
  runCommand(command: CanvasCommand): void {
    command
      .execute(this.argsFor(command))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onCommandExecuted(command))
  }

  /** Execute a command by its id (used by keyboard shortcuts). */
  runCommandById(id: string): void {
    const command = this.commands.find(cmd => cmd.id === id)
    if (command) this.runCommand(command)
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit()
  }

  onContextMenu(event: MouseEvent): void {
    event.preventDefault()
    this.contextMenuPosition = { x: event.clientX, y: event.clientY }
    this.contextMenuTrigger.openMenu()
  }
}
